export const GHOST_BODIES = ["classic", "chubby", "tall", "wisp", "bell", "bean"] as const;
export const GHOST_HEADS = [
  "cap",
  "beanie",
  "tophat",
  "headset",
  "bow",
  "sprout",
  "crown",
] as const;
export const GHOST_FACES = ["round", "nerd", "shades", "monocle"] as const;
export const GHOST_NECKS = ["tie", "bowtie", "suit", "scarf", "lanyard"] as const;
export const GHOST_HANDS = ["coffee", "clipboard", "tablet", "laptop"] as const;
export const GHOST_MOUTHS = ["none", "smile", "grin"] as const;

/** Fabric colors for hats, ties and scarves; stored by index. */
export const GHOST_CLOTH_COLORS = [
  "#F36F68",
  "#7998FF",
  "#A7FF3F",
  "#F16CB1",
  "#78CCA0",
  "#F0CB57",
  "#C4B5FF",
  "#FF914F",
] as const;

export type GhostBody = (typeof GHOST_BODIES)[number];
export type GhostHead = (typeof GHOST_HEADS)[number];
export type GhostFace = (typeof GHOST_FACES)[number];
export type GhostNeck = (typeof GHOST_NECKS)[number];
export type GhostHand = (typeof GHOST_HANDS)[number];
export type GhostMouth = (typeof GHOST_MOUTHS)[number];

export interface GhostSpec {
  /** Body hex; omitted means the identity color. */
  color?: string;
  body: GhostBody;
  head: GhostHead | null;
  face: GhostFace | null;
  neck: GhostNeck | null;
  hand: GhostHand | null;
  /** Index into GHOST_CLOTH_COLORS. */
  cloth: number;
  mouth: GhostMouth;
  cheeks: boolean;
}

export const GHOST_AVATAR_PREFIX = "ghost:1";

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function oneOf<T extends string>(list: readonly T[], value: string | undefined): T | null {
  return value !== undefined && (list as readonly string[]).includes(value) ? (value as T) : null;
}

/** `ghost:1;c=#875DEF;b=bell;h=tophat;a=2;m=smile;k=1`; empty slots are left out. */
export function encodeGhostAvatar(spec: GhostSpec): string {
  const parts = [GHOST_AVATAR_PREFIX];
  if (spec.color) parts.push(`c=${spec.color}`);
  parts.push(`b=${spec.body}`);
  if (spec.head) parts.push(`h=${spec.head}`);
  if (spec.face) parts.push(`f=${spec.face}`);
  if (spec.neck) parts.push(`n=${spec.neck}`);
  if (spec.hand) parts.push(`p=${spec.hand}`);
  parts.push(`a=${spec.cloth}`);
  if (spec.mouth !== "none") parts.push(`m=${spec.mouth}`);
  if (spec.cheeks) parts.push("k=1");
  return parts.join(";");
}

/** Strict parse: any unknown key or value rejects the whole value. */
export function parseGhostAvatar(raw: string): GhostSpec | null {
  const [prefix, ...parts] = raw.split(";");
  if (prefix !== GHOST_AVATAR_PREFIX) return null;
  const fields = new Map<string, string>();
  for (const part of parts) {
    const eq = part.indexOf("=");
    if (eq <= 0) return null;
    const key = part.slice(0, eq);
    if (fields.has(key)) return null;
    fields.set(key, part.slice(eq + 1));
  }
  const known = new Set(["c", "b", "h", "f", "n", "p", "a", "m", "k"]);
  for (const key of fields.keys()) if (!known.has(key)) return null;

  const color = fields.get("c");
  if (color !== undefined && !HEX.test(color)) return null;
  const body = oneOf(GHOST_BODIES, fields.get("b"));
  if (!body) return null;
  const slot = <T extends string>(key: string, list: readonly T[]): T | null | undefined => {
    const value = fields.get(key);
    if (value === undefined) return null;
    return oneOf(list, value) ?? undefined;
  };
  const head = slot("h", GHOST_HEADS);
  const face = slot("f", GHOST_FACES);
  const neck = slot("n", GHOST_NECKS);
  const hand = slot("p", GHOST_HANDS);
  if (head === undefined || face === undefined || neck === undefined || hand === undefined) {
    return null;
  }
  const rawCloth = fields.get("a") ?? "0";
  if (!/^\d$/.test(rawCloth) || Number(rawCloth) >= GHOST_CLOTH_COLORS.length) return null;
  const mouth = fields.has("m") ? oneOf(GHOST_MOUTHS, fields.get("m")) : "none";
  if (!mouth) return null;
  const cheeks = fields.get("k");
  if (cheeks !== undefined && cheeks !== "1") return null;
  return {
    ...(color ? { color } : {}),
    body,
    head,
    face,
    neck,
    hand,
    cloth: Number(rawCloth),
    mouth,
    cheeks: cheeks === "1",
  };
}

export function isGhostAvatarValue(raw: string): boolean {
  return parseGhostAvatar(raw) !== null;
}

export function ghostSeed(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16777619);
  }
  return hash >>> 0;
}

function random(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value = (value + 1831565813) | 0;
    let next = Math.imul(value ^ (value >>> 15), 1 | value);
    next = (next + Math.imul(next ^ (next >>> 7), 61 | next)) ^ next;
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(next: () => number, list: readonly T[]): T {
  return list[Math.floor(next() * list.length) % list.length] as T;
}

/** The look a bot gets until someone dresses it; stable per seed. */
export function randomGhostSpec(seed: number): GhostSpec {
  const next = random(seed);
  const body = pick(next, GHOST_BODIES);
  const head = next() < 0.45 ? null : pick(next, GHOST_HEADS);
  const face = next() < 0.6 ? null : pick(next, GHOST_FACES);
  let neck = next() < 0.5 ? null : pick(next, GHOST_NECKS);
  const hand = next() < 0.45 ? null : pick(next, GHOST_HANDS);
  if (head && face && neck) neck = null;
  const cloth = Math.floor(next() * GHOST_CLOTH_COLORS.length) % GHOST_CLOTH_COLORS.length;
  const mouth = pick(next, ["none", "none", "smile", "smile", "grin"] as const);
  const cheeks = next() < 0.4;
  return { body, head, face, neck, hand, cloth, mouth, cheeks };
}

/**
 * Resolves any stored avatar value to a ghost. Dressed values keep their outfit;
 * legacy hex or `::shape_N` values get the identity's random outfit, with the old
 * shape index choosing the body so a picked shape stays distinct.
 */
export function resolveGhostSpec(
  value: string,
  identity: string,
  legacy?: { shapeIndex?: number },
): GhostSpec {
  const dressed = parseGhostAvatar(value);
  if (dressed) return dressed;
  const spec = randomGhostSpec(ghostSeed(identity || value || "ghost"));
  if (legacy?.shapeIndex !== undefined) {
    spec.body = GHOST_BODIES[legacy.shapeIndex % GHOST_BODIES.length] ?? spec.body;
  }
  return spec;
}

/** The body color to resolve: the dressed color, or the hex of a legacy value. */
export function ghostBodyColor(value: string, spec: GhostSpec): string | undefined {
  return spec.color ?? (value.startsWith("#") ? value.split("::shape_")[0] : undefined);
}

/** How agents describe a dressed ghost when they change their own avatar. */
export const GHOST_AVATAR_HINT = [
  `A dressed ghost like ${GHOST_AVATAR_PREFIX};c=#875DEF;b=bell;h=tophat;f=round;n=tie;p=coffee;a=2;m=smile.`,
  `c: body hex color. b: body, one of ${GHOST_BODIES.join("|")}.`,
  `h: hat, one of ${GHOST_HEADS.join("|")}. f: face, one of ${GHOST_FACES.join("|")}.`,
  `n: neck, one of ${GHOST_NECKS.join("|")}. p: held item, one of ${GHOST_HANDS.join("|")}.`,
  `a: outfit color 0-${GHOST_CLOTH_COLORS.length - 1}. m: smile|grin. k=1 adds cheeks. Leave a key out for none.`,
].join(" ");
